import { Zap } from "lucide-react";
import { useMemo } from "react";
import { isDayInCurrentWeekStreak } from "@/utils/studyStreak";
import { useTranslation } from "react-i18next";
import { getLocalizedWeekdayLabels } from "@/utils/weekdays";

interface StudyStreakCardProps {
  streakDays?: number;
  streakEndsToday?: boolean;
}

interface CompactStudyStreakProps {
  streakDays?: number;
}

export function CompactStudyStreak({ streakDays = 0 }: CompactStudyStreakProps) {
  const { t } = useTranslation();

  return (
    <section
      aria-label={t("study.day_streak", { count: streakDays })}
      className="flex h-11 shrink-0 items-center gap-3 rounded-[28px] border border-[#dddfe4] bg-white px-4"
    >
      <span className="text-lg font-bold leading-[27px] text-[#0a0c11]">{streakDays}</span>
      <Zap aria-hidden="true" className="h-6 w-[15px] fill-[#f5a000] text-[#f5a000]" />
    </section>
  );
}

export function StudyStreakCard({ streakDays = 0, streakEndsToday = false }: StudyStreakCardProps) {
  const { i18n, t } = useTranslation();
  const currentDayIndex = useMemo(() => (new Date().getDay() + 6) % 7, []);
  const dayLabels = useMemo(() => getLocalizedWeekdayLabels(i18n.language), [i18n.language]);

  return (
    <section className="dp-card flex min-h-[76px] w-fit max-w-full min-w-0 items-center gap-3 px-4 py-3 sm:gap-4 xl:gap-6">
      <div className="min-w-0">
        <p className="font-semibold leading-6 text-[#0a0c11]">{t("study.day_streak", { count: streakDays })} 🔥</p>
        <p className="text-sm leading-[21px] tracking-[0.07px] text-[#5b616d]">{t("study.keep_going")}</p>
      </div>
      <div className="min-w-0 shrink-0 overflow-x-auto">
        <div className="grid w-max grid-cols-7 gap-1 xl:gap-2">
          {dayLabels.map((label, index) => (
            <div className="flex flex-col items-center gap-1" key={`${label}-${index}`}>
              <span className="text-xs font-medium text-[#5b616d]">{label}</span>
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-full ${
                  isDayInCurrentWeekStreak(index, currentDayIndex, streakDays, streakEndsToday)
                    ? "bg-[var(--dp-brand-button)] text-white"
                    : "border border-[#d6d9de] text-[#c2c6cc]"
                }`}
              >
                <Zap aria-hidden="true" className="h-3.5 w-3.5 fill-current" />
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
